import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ensureDefined } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';
import type { Id, PrintStyleId, ReportWorkflowId } from '@e-socna/data-layer';

import { useDocumentEditorContext } from '../../components/DocumentEditor/contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { PATH_NOT_FOUND } from '../../app/constants/paths.constant';
import { RightDrawer } from '../../components/RightDrawer';
import { MissionReportInfoForm } from '../../forms/MissionReportInfo';
import { Code } from '../../components/Code';
import { FormVariant } from '../../forms';
import { MissionReportInfoStateForm } from '../../forms/MissionReportInfoState';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';

import { useFindAllDocumentTemplatesQuery } from '../DocumentAndSectionTemplates/hooks/useFindAllDocumentTemplatesQuery.hook';

import type { MissionReportWithItsInfo } from './types/mission-report-with-its-info.type';

export const MissionReportInfoTabPanel = () => {
  const {
    props: {
      routerSettings: { editorModePaths },
      handlers,
    },
    state,
    actions,
  } = useDocumentEditorContext<MissionReportWithItsInfo>();

  const { printStylesIdMap, reportWorkflowsIdMap } = useReferencesContext();

  const { data: documentTemplatesData } = useFindAllDocumentTemplatesQuery();

  const navigate = useNavigate();
  const { pathname: currentPath } = useLocation();

  const { missionId } = useMissionReadyContext();

  const leaveEditionMode = useCallback(
    () => void navigate(editorModePaths.Info ?? PATH_NOT_FOUND),
    [editorModePaths.Info, navigate],
  );

  const enterStateEditionMode = useCallback(
    () => void navigate(editorModePaths.InfoEditState ?? PATH_NOT_FOUND),
    [navigate, editorModePaths.InfoEditState],
  );

  const isInStateEditionMode = currentPath === editorModePaths.InfoEditState;

  const { editable } = state;
  const {
    code,
    codingConfig,
    info,
    printing: { printSettings },
    templateId,
    title,
  } = editable;

  const useSubmitFactory = useCallback(
    (callback?: () => void) => (values: Readonly<MissionReportWithItsInfo>) => {
      actions.updateInfo(values);
      void handlers.onSavePrintSettings?.(values.printing.printSettings);
      callback?.();
    },
    [actions, handlers],
  );

  const { reportWorkflow, printStyle } = ensureDefined({
    reportWorkflow: reportWorkflowsIdMap[info.workflowId as ReportWorkflowId],
    printStyle: printStylesIdMap[printSettings.printStyleId as PrintStyleId],
  });

  const template = documentTemplatesData?.findAllDocumentTemplates.find(
    (candidate) => candidate._id === templateId,
  );

  const templateLabel = template
    ? `${template.code} - ${template.label}`
    : 'Aucun';

  const handleSuccess = useCallback(
    ({ newStateId }: { actionId: Id; newStateId: Id }) => {
      actions.updateInfo(
        {
          ...editable,
          info: {
            ...editable.info,
            stateId: newStateId,
          },
        },
        false,
      );
      actions.bumpLatestSavedVersion();
    },
    [actions, editable],
  );

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <MissionReportInfoStateForm
          enterEditionMode={enterStateEditionMode}
          isInEditionMode={isInStateEditionMode}
          leaveEditionMode={leaveEditionMode}
          missionId={missionId}
          missionReportInfo={info}
          onSuccess={handleSuccess}
          reportId={info.reportId ?? EMPTY_ID}
        />
        <Typography gutterBottom variant="h6">
          Informations du Rapport
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Typography gutterBottom variant="body1">
              Workflow : {reportWorkflow.label}
            </Typography>
          </Box>
        </Paper>
        <Typography gutterBottom variant="h6">
          Document
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Typography gutterBottom variant="body1">
              Modèle de document : {templateLabel}
            </Typography>
            <Code code={code} codingConfig={codingConfig} />
            <Typography gutterBottom variant="body1">
              Titre : {!title || title === EMPTY_STRING ? 'Non défini' : title}
            </Typography>
          </Box>
        </Paper>
        <Typography gutterBottom variant="h6">
          Options d&apos;impression
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Typography gutterBottom variant="body1">
              Inclure entêtes de pages :{' '}
              {printSettings.addHeaders ? 'Oui' : 'Non'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Inclure pieds de pages :{' '}
              {printSettings.addFooters ? 'Oui' : 'Non'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Style d&apos;impression : {printStyle.label}
            </Typography>
          </Box>
        </Paper>
      </Stack>
      <RightDrawer
        isOpen={currentPath === editorModePaths.InfoEdit}
        onDrawerClose={leaveEditionMode}
      >
        <MissionReportInfoForm
          formVariant={FormVariant.Modification}
          initialValues={editable}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          showCancelButton
          useSubmitFactory={useSubmitFactory}
        />
      </RightDrawer>
    </>
  );
};
